import { useMitarbeiterAppForm } from '@/domain/mitarbeiter/shared/components/form/hooks';
import { anlageassistentFormDefaultValues } from '@/domain/mitarbeiter/features/anlageassistent/context/AnlageassistentFormDefaultValues';
import { useFormSchema } from '@/domain/mitarbeiter/features/anlageassistent/hooks/schema/useFormSchema';
import { useCreateMitarbeiter } from '@/domain/mitarbeiter/features/anlageassistent/hooks/useCreateMitarbeiter/useCreateMitarbeiter';
import { getWarningSearchParams } from '@/domain/mitarbeiter/features/anlageassistent/utils/getWarningSearchParams';
import { mapFormToInput } from '@/domain/mitarbeiter/features/anlageassistent/utils/mappings/mapFormToInput';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { toast } from '@/hooks/use-toast';
import { createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

type AnlageassistentFormContextType = ReturnType<typeof useAnlageassistentForm>;

const AnlageassistentFormContext =
  createContext<AnlageassistentFormContextType | null>(null);

function useAnlageassistentForm() {
  const FormSchema = useFormSchema();
  const { t } = useTranslation();
  const { currentStandort } = useStandortContext();
  const [createMitarbeier] = useCreateMitarbeiter();
  const navigate = useNavigate();
  return useMitarbeiterAppForm({
    formId: 'ma-wizard',
    defaultValues: anlageassistentFormDefaultValues,
    validators: {
      onSubmit: FormSchema,
    },
    // eslint-disable-next-line complexity
    onSubmit: async ({ value }) => {
      const mappedInput = mapFormToInput({ values: value, t });
      try {
        const res = await createMitarbeier({
          standortId: currentStandort.id,
          input: mappedInput,
        });

        if (res.error || res.data?.mitarbeiter_mitarbeiterCreate.error) {
          const apiError =
            res.error ?? res.data?.mitarbeiter_mitarbeiterCreate.error;
          toast({
            title: t('domain.mitarbeiter.create.errors.message'),
            description: apiError?.message,
          });
          return;
        }

        if (
          res.data?.mitarbeiter_mitarbeiterCreate.validationErrors &&
          res.data.mitarbeiter_mitarbeiterCreate.validationErrors?.length > 0
        ) {
          const validationError =
            res.data.mitarbeiter_mitarbeiterCreate.validationErrors[0];
          toast({
            title: t('domain.mitarbeiter.create.errors.message'),
            description: t(
              'domain.mitarbeiter.create.errors.validationErrors.' +
                validationError.errorCode,
            ),
          });
          return;
        }

        const searchParams = getWarningSearchParams(
          res.data?.mitarbeiter_mitarbeiterCreate,
        );
        navigate({
          pathname: '/mitarbeiter/create/success',
          search: searchParams ? `?${searchParams}` : undefined,
        });
      } catch {
        toast({
          title: t('domain.mitarbeiter.create.errors.message'),
        });
      }
    },
  });
}

export function AnlageassistentFormProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const form = useAnlageassistentForm();
  return (
    <AnlageassistentFormContext.Provider value={form}>
      {children}
    </AnlageassistentFormContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAnlageassistentFormContext() {
  const context = useContext(AnlageassistentFormContext);
  if (!context) {
    throw new Error('AnlageassistentFormProvider missing');
  }
  return context;
}
